import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import P11AdminLayout from "./P11AdminLayout";
import { useP11Role } from "./P11AdminGuard";
import { P11, fmtDate } from "./p11shared";

// PROJECT MANAGEMENT — capaciteit (project + per sessie), registratie-switches, finalize, archive.
export default function P11AdminProject() {
  const role = useP11Role();
  const [data, setData] = useState(null);
  const [cap, setCap] = useState("");
  const [sessionCaps, setSessionCaps] = useState({});
  const [confirm, setConfirm] = useState(null); // finalize | archive
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/project11/admin/project").then(({ data }) => {
    setData(data);
    setCap(String(data.edition.project_capacity));
    setSessionCaps(Object.fromEntries(data.sessions.map((s) => [s.id, String(s.capacity)])));
  }).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    setBusy(true);
    try {
      await api.put("/project11/admin/project", payload);
      toast.success("Opgeslagen");
      load();
    } catch (err) {
      const d = err?.response?.data?.detail || "";
      if (d.startsWith("CAPACITY_CONFLICT")) toast.error("Verlaging conflicteert met bestaande bevestigde plekken — los dit eerst op");
      else if (d.startsWith("SESSION_CAPACITY_CONFLICT")) toast.error(`Sessiecapaciteit te laag voor bestaande reserveringen (${d.split(":")[2]} gereserveerd)`);
      else toast.error(d || "Opslaan mislukt");
    } finally { setBusy(false); }
  };

  const doAction = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/project11/admin/project/${action}`);
      toast.success(action === "finalize" ? `Gefinaliseerd: ${data.completed} voltooid, ${data.incomplete} niet voltooid` : "Gearchiveerd");
      setConfirm(null);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(d === "NOT_ALL_SESSIONS_FINISHED" ? "Nog niet alle acht sessies zijn geweest" : (typeof d === "string" ? d : "Mislukt"));
    } finally { setBusy(false); }
  };

  if (!data) return <P11AdminLayout><span className="p11-label">…</span></P11AdminLayout>;
  const e = data.edition;
  const isOwner = role === "owner";

  return (
    <P11AdminLayout>
      <div data-testid="p11-admin-project">
        <div className="p11-label-yellow">PROJECT MANAGEMENT → CAPACITY</div>
        <h1 className="p11-h1 text-3xl mt-4 mb-2">{e.id}.</h1>
        <div className="p11-label mb-10" data-testid="p11-project-status">STATUS: {(e.status || "").toUpperCase()}{e.finalized_at ? ` · GEFINALISEERD ${e.finalized_at.slice(0, 10)}` : ""}{e.archived_at ? ` · GEARCHIVEERD ${e.archived_at.slice(0, 10)}` : ""}</div>

        <section className="border p-6 mb-8" style={{ borderColor: P11.border }} data-testid="p11-project-capacity">
          <div className="p11-label mb-4">// PROJECT CAPACITY</div>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <input type="number" min="1" max="500" value={cap} onChange={(ev) => setCap(ev.target.value)} disabled={!isOwner || e.status === "archived"} className="p11-input !w-32 text-2xl p11-mono" data-testid="p11-cap-project" />
              <div className="p11-label mt-2">MAX BEVESTIGDE DEELNEMERS · NU {e.confirmed_count}</div>
            </div>
            {isOwner && e.status !== "archived" && (
              <button onClick={() => save({ project_capacity: parseInt(cap, 10) })} disabled={busy} className="p11-btn-ghost" data-testid="p11-cap-project-save">OPSLAAN</button>
            )}
          </div>
          <p className="p11-label mt-4" style={{ color: P11.gray }}>Hogere capaciteit vergroot ook het nummerbereik (#001—#{String(parseInt(cap || "21", 10)).padStart(3, "0")}). Sessiecapaciteit blijft apart.</p>
        </section>

        <section className="border p-6 mb-8" style={{ borderColor: P11.border }} data-testid="p11-session-capacity">
          <div className="p11-label mb-4">// SESSION CAPACITY (PER SESSIE)</div>
          <div className="space-y-3">
            {data.sessions.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-4" data-testid={`p11-cap-session-${s.number}`}>
                <span className="p11-label w-40">SESSIE {String(s.number).padStart(2, "0")} · {fmtDate(s.date)}</span>
                <input type="number" min="1" max="500" value={sessionCaps[s.id] ?? s.capacity} onChange={(ev) => setSessionCaps({ ...sessionCaps, [s.id]: ev.target.value })} disabled={!isOwner || e.status === "archived"} className="p11-input !w-24 p11-mono" data-testid={`p11-cap-session-input-${s.number}`} />
                <span className="p11-label" style={{ color: P11.gray2 }}>{s.reserved_count} GERESERVEERD</span>
              </div>
            ))}
          </div>
          {isOwner && e.status !== "archived" && (
            <button onClick={() => save({ session_capacities: Object.fromEntries(Object.entries(sessionCaps).map(([k, v]) => [k, parseInt(v, 10)])) })} disabled={busy} className="p11-btn-ghost mt-6" data-testid="p11-cap-sessions-save">SESSIECAPACITEIT OPSLAAN</button>
          )}
        </section>

        <section className="border p-6 mb-8" style={{ borderColor: P11.border }} data-testid="p11-registration-flags">
          <div className="p11-label mb-4">// REGISTRATIE</div>
          <div className="flex flex-wrap gap-6">
            {[["registration_open", "REGISTRATIE OPEN"], ["waitlist_open", "WACHTLIJST OPEN"]].map(([key, label]) => (
              <label key={key} className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={!!e[key]} disabled={!isOwner} onChange={(ev) => save({ [key]: ev.target.checked })} className="accent-[#FFE44D]" data-testid={`p11-flag-${key}`} />
                <span className="p11-label">{label}</span>
              </label>
            ))}
          </div>
        </section>

        {isOwner && e.status !== "archived" && (
          <section className="border p-6" style={{ borderColor: P11.yellow }} data-testid="p11-project-lifecycle">
            <div className="p11-label-yellow mb-4">// FINALIZE & ARCHIVE</div>
            <p className="text-sm mb-6" style={{ color: P11.gray2 }}>Finaliseren berekent eindstatussen (8/8 → COMPLETED) en informeert deelnemers. Archiveren bevriest het project als historisch record.</p>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => setConfirm("finalize")} className="p11-btn" data-testid="p11-finalize-btn">FINALIZE PROJECT</button>
              {e.status === "finalized" && <button onClick={() => setConfirm("archive")} className="p11-btn-ghost" data-testid="p11-archive-btn">ARCHIVE PROJECT</button>}
            </div>
          </section>
        )}

        {confirm && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setConfirm(null)}>
            <div className="bg-[#080808] border max-w-sm w-full p-8" style={{ borderColor: P11.border }} onClick={(ev) => ev.stopPropagation()} data-testid="p11-lifecycle-confirm">
              <div className="p11-label-yellow mb-4">// BEVESTIGEN</div>
              <p className="text-sm" style={{ color: P11.gray2 }}>
                {confirm === "finalize" ? "Project finaliseren? Controleer eerst dat alle aanwezigheid is verwerkt. De actie is idempotent en wordt vastgelegd." : "Project archiveren? Er kunnen daarna geen nieuwe inschrijvingen of aanwezigheidswijzigingen meer plaatsvinden."}
              </p>
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => setConfirm(null)} className="p11-btn-ghost" data-testid="p11-lifecycle-no">ANNULEREN</button>
                <button onClick={() => doAction(confirm)} disabled={busy} className="p11-btn" data-testid="p11-lifecycle-yes">{busy ? "…" : "BEVESTIGEN"}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </P11AdminLayout>
  );
}
